import {Platform} from 'react-native';
import Styles from 'src/components/Styles';
import Settings from 'src/Settings';
import {BuildTypeEnum} from 'src/constants/BuildTypeEnum';
import {getDescriber as getAccountDescriber} from 'src/components/elements/account/descriptor/DescriptorType';
import moment from 'moment';

export interface CardColors {
  backgroundColor: string;
  borderColor: string;
}

interface StyleConfig {
  cardColors: {
    backgroundColor: () => string;
    borderColor: () => string;
  };
  textColor: () => string;
  iconBackground: (() => string) | null;
  logoSize: number;
}

const STYLE_CONFIG: Partial<Record<BuildTypeEnum, StyleConfig>> = {
  [BuildTypeEnum.default]: {
    cardColors: {
      backgroundColor: () => Styles.Colors.PayNew.blueHuesBase07,
      borderColor: () => Styles.Colors.PayNew.primary01,
    },
    textColor: () => Styles.Colors.PayNew.subsidyCardTextColor,
    iconBackground: () => Styles.Colors.PayNew.blueHuesBase07,
    logoSize: 50,
  },

  [BuildTypeEnum.canteen]: {
    cardColors: {
      backgroundColor: () => Styles.Colors.Canteen.subsidyCardBackground,
      borderColor: () => Styles.Colors.Canteen.green,
    },
    textColor: () => Styles.Colors.Canteen.primary,
    iconBackground: () => Styles.Colors.Canteen.lightGray,
    logoSize: 50,
  },

  [BuildTypeEnum.companykitchen]: {
    cardColors: {
      backgroundColor: () => Styles.Colors.CompanyKitchen.subsidyCardBackground,
      borderColor: () => Styles.Colors.CompanyKitchen.primary,
    },
    textColor: () => Styles.Colors.CompanyKitchen.black,
    iconBackground: null,
    logoSize: 50,
  },

  [BuildTypeEnum.refive]: {
    cardColors: {
      backgroundColor: () => Styles.Colors.Refive.subsidyCardBackground,
      borderColor: () => Styles.Colors.Refive.toastBorderColor,
    },
    textColor: () => Styles.Colors.Refive.navy,
    iconBackground: null,
    logoSize: 50,
  },

  [BuildTypeEnum.aramark]: {
    cardColors: {
      backgroundColor: () => Styles.Colors.Aramark.lightRed,
      borderColor: () => Styles.Colors.Aramark.primary,
    },
    textColor: () => Styles.Colors.Aramark.black01,
    iconBackground: null,
    logoSize: 50,
  },
};

const getDefaultCardColors = (): CardColors => {
  const promoBg =
    getAccountDescriber().getBalanceItemStyleDescriptor().promoContainer
      ?.backgroundColor;

  const fallbackBg =
    typeof promoBg === 'string' && promoBg.trim() !== ''
      ? promoBg
      : Styles.Colors.PayNew.blueHuesBase07;

  return {
    backgroundColor: fallbackBg,
    borderColor: Styles.Colors.PayNew.primary01,
  };
};

const getDefaultTextColor = (): string => {
  const balanceTypeColor =
    getAccountDescriber().balanceTypeStyleDescriptor().color;
  return (
    (typeof balanceTypeColor === 'string' ? balanceTypeColor : null) ||
    (typeof Styles.tabBarTextColor === 'string'
      ? Styles.tabBarTextColor
      : null) ||
    (typeof Styles.darkColor === 'string' ? Styles.darkColor : '#000000')
  );
};

const getDefaultIconBackground = (): string => {
  const promoBg =
    getAccountDescriber().getBalanceItemStyleDescriptor().promoContainer
      ?.backgroundColor;
  return (
    typeof promoBg === 'string' ? promoBg : Styles.tabBarBackgroundColor
  ) as string;
};

export const getCardColors = (): CardColors => {
  const buildType = Settings.buildType;

  if (!buildType) {
    return getDefaultCardColors();
  }

  const config = STYLE_CONFIG[buildType as BuildTypeEnum];

  if (!config || !config.cardColors) {
    return getDefaultCardColors();
  }

  try {
    const backgroundColor = config.cardColors.backgroundColor();
    const borderColor = config.cardColors.borderColor();

    if (
      backgroundColor &&
      typeof backgroundColor === 'string' &&
      backgroundColor.trim() !== '' &&
      borderColor &&
      typeof borderColor === 'string' &&
      borderColor.trim() !== ''
    ) {
      return {
        backgroundColor,
        borderColor,
      };
    }
  } catch (error) {}

  return getDefaultCardColors();
};

export const getTextColor = (): string => {
  const config = STYLE_CONFIG[Settings.buildType];
  return config?.textColor ? config.textColor() : getDefaultTextColor();
};

export const getIconBackgroundColor = (): string => {
  const config = STYLE_CONFIG[Settings.buildType];

  if (!config) {
    return getDefaultIconBackground();
  }

  if (!config.iconBackground) {
    return 'transparent';
  }

  return config.iconBackground();
};

export const getLogoSize = (): number => {
  const config = STYLE_CONFIG[Settings.buildType];
  return config?.logoSize ?? 50;
};

export const formatSubsidyDate = (dateString?: string): string | undefined => {
  if (!dateString) {
    return undefined;
  }

  try {
    let dateMoment: moment.Moment;

    if (dateString.includes('/')) {
      dateMoment = moment(dateString, ['MM/DD/YYYY', 'MM/DD/YY']);
    } else {
      dateMoment = moment(dateString, ['YYYY-MM-DD', 'YYYY-MM-DD HH:mm:ss']);
    }

    if (!dateMoment.isValid()) {
      return dateString;
    }

    return dateMoment.format('MM/DD/YYYY');
  } catch (error) {
    return dateString;
  }
};

export interface SubsidyCardStyles {
  cardColors: CardColors;
  textColor: string;
  iconBackgroundColor: string;
  logoSize: number;
}

export const getSubsidyCardStyles = (): SubsidyCardStyles => ({
  cardColors: getCardColors(),
  textColor: getTextColor(),
  iconBackgroundColor: getIconBackgroundColor(),
  logoSize: getLogoSize(),
});

export const getSubsidyPageBackgroundColor = (): string => {
  if (Platform.OS === 'web') {
    return Styles.white;
  }

  if (Settings.buildType === BuildTypeEnum.aramark) {
    return (
      Styles.Colors.Aramark.neutralHuesBase13 || Styles.tabBarBackgroundColor
    );
  }
  if (Settings.buildType === BuildTypeEnum.companykitchen) {
    return Styles.tabBarBackgroundColor;
  }
  if (Settings.buildType === BuildTypeEnum.canteen) {
    return Styles.Colors.Canteen.lightGray || Styles.tabBarBackgroundColor;
  }
  return Styles.white;
};
